import { getValidAccessToken, triggerSilentRefresh } from '@services/auth';
import { ResourceItem } from '@store';
import {
  DriveFile,
  DriveFileListResponse,
  SpreadsheetDetailsResponse,
  SheetValuesResponse,
  GoogleCache,
} from './types';
import { loadCache, saveCache } from './util';

/**
 * Service class that manages resources data in Google Drive and Sheets.
 * Stores all resources in a single spreadsheet under GrowBoard/Resources/ folder.
 */
class GoogleSheetsResourceService {
  private cache: GoogleCache = loadCache();

  /**
   * Performs an authenticated request to a Google API endpoint.
   * On a 401 or 403, attempts a silent token refresh and retries once
   * before throwing.
   */
  private async fetchAPI<T>(
    url: string,
    init?: RequestInit,
    isRetry = false,
  ): Promise<T> {
    const token = getValidAccessToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    };

    const res = await fetch(url, {
      ...init,
      headers,
    });

    if (!res.ok) {
      if ((res.status === 401 || res.status === 403) && !isRetry) {
        try {
          await triggerSilentRefresh();
          return this.fetchAPI<T>(url, init, true);
        } catch {
          this.cache = { spreadsheetIds: {}, sheetIds: {} };
          saveCache(this.cache);
          throw new Error(
            `Google API Authentication Error (${res.status}): Session expired. Please re-login.`,
          );
        }
      }

      let errDetail = '';
      try {
        const errJson = await res.clone().json();
        errDetail =
          (errJson as { error?: { message?: string } })?.error?.message ||
          JSON.stringify(errJson);
      } catch {
        try {
          errDetail = await res.clone().text();
        } catch {
          errDetail = 'No error details available';
        }
      }

      if (res.status === 401 || res.status === 403) {
        this.cache = { spreadsheetIds: {}, sheetIds: {} };
        saveCache(this.cache);
        throw new Error(
          `Google API Authentication Error (${res.status}): ${errDetail}. Please re-login.`,
        );
      }

      throw new Error(`Google API Error (${res.status}): ${errDetail}`);
    }

    if (res.status === 204) return null as unknown as T;
    return res.json() as Promise<T>;
  }

  // ── DRIVE OPERATIONS ────────────────────────────────────────────────────────

  /**
   * Resolves the "Growboard" root folder in the user's Drive, creating it if absent.
   */
  private async getOrCreateGrowboardFolder(): Promise<string> {
    if (this.cache.growboardFolderId) {
      return this.cache.growboardFolderId;
    }

    console.log('Searching for Growboard folder in Drive...');
    const searchUrl = `https://www.googleapis.com/drive/v3/files?q=name='Growboard' and mimeType='application/vnd.google-apps.folder' and 'root' in parents and trashed=false&fields=files(id)`;
    const result = await this.fetchAPI<DriveFileListResponse>(searchUrl);

    if (result.files && result.files.length > 0) {
      this.cache.growboardFolderId = result.files[0].id;
      saveCache(this.cache);
      return result.files[0].id;
    }

    console.log('Creating Growboard folder in Drive...');
    const createUrl = 'https://www.googleapis.com/drive/v3/files';
    const folder = await this.fetchAPI<DriveFile>(createUrl, {
      method: 'POST',
      body: JSON.stringify({
        name: 'Growboard',
        mimeType: 'application/vnd.google-apps.folder',
      }),
    });

    this.cache.growboardFolderId = folder.id;
    saveCache(this.cache);
    return folder.id;
  }

  /**
   * Resolves the "Resources" subfolder inside the Growboard folder, creating it if absent.
   */
  private async getOrCreateResourcesFolder(
    growboardFolderId: string,
  ): Promise<string> {
    if (this.cache.resourcesFolderId) {
      return this.cache.resourcesFolderId;
    }

    console.log('Searching for Resources folder in Growboard...');
    const searchUrl = `https://www.googleapis.com/drive/v3/files?q=name='Resources' and mimeType='application/vnd.google-apps.folder' and '${growboardFolderId}' in parents and trashed=false&fields=files(id)`;
    const result = await this.fetchAPI<DriveFileListResponse>(searchUrl);

    if (result.files && result.files.length > 0) {
      this.cache.resourcesFolderId = result.files[0].id;
      saveCache(this.cache);
      return result.files[0].id;
    }

    console.log('Creating Resources folder in Growboard...');
    const createUrl = 'https://www.googleapis.com/drive/v3/files';
    const folder = await this.fetchAPI<DriveFile>(createUrl, {
      method: 'POST',
      body: JSON.stringify({
        name: 'Resources',
        mimeType: 'application/vnd.google-apps.folder',
        parents: [growboardFolderId],
      }),
    });

    this.cache.resourcesFolderId = folder.id;
    saveCache(this.cache);
    return folder.id;
  }

  /**
   * Resolves the "Resources" spreadsheet inside the Resources folder, creating it if absent.
   */
  private async getOrCreateResourcesSpreadsheet(
    resourcesFolderId: string,
  ): Promise<string> {
    if (this.cache.resourcesSpreadsheetId) {
      return this.cache.resourcesSpreadsheetId;
    }

    console.log('Searching for Resources spreadsheet...');
    const searchUrl = `https://www.googleapis.com/drive/v3/files?q=name='Resources' and mimeType='application/vnd.google-apps.spreadsheet' and '${resourcesFolderId}' in parents and trashed=false&fields=files(id)`;
    const result = await this.fetchAPI<DriveFileListResponse>(searchUrl);

    if (result.files && result.files.length > 0) {
      const spreadsheetId = result.files[0].id;
      this.cache.resourcesSpreadsheetId = spreadsheetId;
      saveCache(this.cache);
      return spreadsheetId;
    }

    console.log('Creating Resources spreadsheet...');
    const createUrl = 'https://www.googleapis.com/drive/v3/files';
    const file = await this.fetchAPI<DriveFile>(createUrl, {
      method: 'POST',
      body: JSON.stringify({
        name: 'Resources',
        mimeType: 'application/vnd.google-apps.spreadsheet',
        parents: [resourcesFolderId],
      }),
    });

    const spreadsheetId = file.id;
    this.cache.resourcesSpreadsheetId = spreadsheetId;
    saveCache(this.cache);

    // Get spreadsheet details to obtain default sheet tab name and rename or use it
    console.log('Writing default headers to Resources spreadsheet...');
    const detailsUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`;
    const spreadsheet =
      await this.fetchAPI<SpreadsheetDetailsResponse>(detailsUrl);
    const sheetTitle = spreadsheet.sheets?.[0]?.properties.title || 'Sheet1';

    // Write header schema row: Id, title, subtitle, link, tags, about_resource
    const writeHeadersUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${sheetTitle}!A1:F1?valueInputOption=RAW`;
    await this.fetchAPI<unknown>(writeHeadersUrl, {
      method: 'PUT',
      body: JSON.stringify({
        values: [['Id', 'title', 'subtitle', 'link', 'tags', 'about_resource']],
      }),
    });

    return spreadsheetId;
  }

  /**
   * Resolves the spreadsheet and folder IDs, clearing cache and retrying once on failure.
   */
  private async getSpreadsheetDetails(): Promise<{
    spreadsheetId: string;
    sheetTitle: string;
  }> {
    try {
      const growboardFolderId = await this.getOrCreateGrowboardFolder();
      const resourcesFolderId =
        await this.getOrCreateResourcesFolder(growboardFolderId);
      const spreadsheetId =
        await this.getOrCreateResourcesSpreadsheet(resourcesFolderId);

      // Fetch spreadsheet details to obtain tab name
      const detailsUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`;
      const spreadsheet =
        await this.fetchAPI<SpreadsheetDetailsResponse>(detailsUrl);
      const sheetTitle = spreadsheet.sheets?.[0]?.properties.title || 'Sheet1';

      return { spreadsheetId, sheetTitle };
    } catch (error) {
      console.warn(
        'Error resolving resources Google sheet, clearing cache and retrying:',
        error,
      );
      // Clear resources specific cache
      delete this.cache.resourcesFolderId;
      delete this.cache.resourcesSpreadsheetId;
      saveCache(this.cache);

      const growboardFolderId = await this.getOrCreateGrowboardFolder();
      const resourcesFolderId =
        await this.getOrCreateResourcesFolder(growboardFolderId);
      const spreadsheetId =
        await this.getOrCreateResourcesSpreadsheet(resourcesFolderId);

      const detailsUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`;
      const spreadsheet =
        await this.fetchAPI<SpreadsheetDetailsResponse>(detailsUrl);
      const sheetTitle = spreadsheet.sheets?.[0]?.properties.title || 'Sheet1';

      return { spreadsheetId, sheetTitle };
    }
  }

  // ── PUBLIC CRUD INTERFACE ──────────────────────────────────────────────────

  /**
   * Fetches all resources from Google Sheets.
   */
  public async getResources(): Promise<{
    data: ResourceItem[];
    status: string;
    successMessage: string;
  }> {
    try {
      const { spreadsheetId, sheetTitle } = await this.getSpreadsheetDetails();

      const valuesUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${sheetTitle}!A2:F`;
      const result = await this.fetchAPI<SheetValuesResponse>(valuesUrl);

      const rows = result.values || [];
      const data: ResourceItem[] = rows.map((row) => ({
        Id: String(row[0] || ''),
        title: String(row[1] || ''),
        subtitle: String(row[2] || ''),
        link: String(row[3] || ''),
        tags: row[4]
          ? String(row[4])
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
        about_resource: String(row[5] || ''),
      }));

      return {
        data,
        status: 'SUCCESS',
        successMessage: `Retrieved ${data.length} resources successfully from Google Sheets.`,
      };
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Failed to fetch resources.';
      console.error('Failed to get resources from Google Sheets:', e);
      return {
        data: [],
        status: 'ERROR',
        successMessage: message,
      };
    }
  }

  /**
   * Appends a new resource row to the sheet.
   */
  public async addResource(
    resource: Omit<ResourceItem, 'Id'>,
  ): Promise<ResourceItem> {
    const { spreadsheetId, sheetTitle } = await this.getSpreadsheetDetails();
    const generatedId = Math.random().toString(36).substring(2, 11);

    const appendUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${sheetTitle}!A:F:append?valueInputOption=RAW`;
    await this.fetchAPI<unknown>(appendUrl, {
      method: 'POST',
      body: JSON.stringify({
        values: [
          [
            generatedId,
            resource.title,
            resource.subtitle || '',
            resource.link,
            resource.tags.join(', '),
            resource.about_resource || '',
          ],
        ],
      }),
    });

    return {
      Id: generatedId,
      ...resource,
    };
  }

  /**
   * Deletes a resource row from the sheet by ID.
   */
  public async deleteResource(id: string): Promise<boolean> {
    const { spreadsheetId, sheetTitle } = await this.getSpreadsheetDetails();

    // Fetch spreadsheet details to obtain sheet tab numeric ID
    const detailsUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets(properties(sheetId,title))`;
    const spreadsheet =
      await this.fetchAPI<SpreadsheetDetailsResponse>(detailsUrl);
    const sheetId = spreadsheet.sheets?.[0]?.properties.sheetId || 0;

    // Fetch all values to find the matching row index
    const valuesUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${sheetTitle}!A:F`;
    const result = await this.fetchAPI<SheetValuesResponse>(valuesUrl);
    const rows = result.values || [];

    const rowIndex = rows.findIndex((row) => row[0] === id);
    if (rowIndex === -1) {
      throw new Error(`Resource with ID ${id} not found in sheet.`);
    }

    // rowIndex is 0-based index.
    const batchUpdateUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`;
    await this.fetchAPI<unknown>(batchUpdateUrl, {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          {
            deleteDimension: {
              range: {
                sheetId,
                dimension: 'ROWS',
                startIndex: rowIndex,
                endIndex: rowIndex + 1,
              },
            },
          },
        ],
      }),
    });

    return true;
  }
}

export const googleSheetsResourceService = new GoogleSheetsResourceService();
export default googleSheetsResourceService;
